// Content Status Badge - displays status tags for content deliverables

import { Badge } from "@repo/ui";
import {
  AlertTriangle,
  Ban,
  Calendar,
  Check,
  Clock,
  Infinity as InfinityIcon,
  RefreshCw,
} from "lucide-react";
import { format, addDays, parseISO } from "date-fns";

// ============================================================================
// Status Badges
// ============================================================================

export function ChangesRequestedBadge() {
  return (
    <Badge variant="destructive" className="text-xs">
      <AlertTriangle className="h-3 w-3 mr-1" />
      Changes Requested
    </Badge>
  );
}

export function ApprovedBadge() {
  return (
    <Badge
      variant="outline"
      className="text-xs text-success-foreground border-success-foreground"
    >
      <Check className="h-3 w-3 mr-1" />
      Approved
    </Badge>
  );
}

export function PendingApprovalBadge() {
  return (
    <Badge variant="secondary" className="text-xs">
      <RefreshCw className="h-3 w-3 mr-1" />
      Pending Approval
    </Badge>
  );
}

export function CompletedBadge() {
  return (
    <Badge
      variant="outline"
      className="text-xs text-success-foreground border-success-foreground"
    >
      <Check className="h-3 w-3 mr-1" />
      Completed
    </Badge>
  );
}

export function ExtraContentBadge() {
  return (
    <Badge
      variant="outline"
      className="text-xs text-success-foreground border-success-foreground"
    >
      Extra Content
    </Badge>
  );
}

// ============================================================================
// Usage Rights Duration Label Formatting
// ============================================================================

const PRESET_DURATION_LABELS: Record<number, string> = {
  90: "3 months",
  180: "6 months",
  365: "1 year",
};

/**
 * Format a usage rights duration into a human-readable label.
 *
 * Priority:
 * 1. If customValue + customUnit provided → use directly (e.g. "2 months")
 * 2. If preset (90/180/365) → use preset label (e.g. "3 months")
 * 3. Else → heuristic from days using the same convention as backend (months=30, years=365)
 */
export function formatDurationLabel(
  days: number,
  customValue?: number | null,
  customUnit?: string | null,
): string {
  // Priority 1: exact custom fields from the agreement
  if (customValue != null && customValue > 0 && customUnit) {
    const unitSingular =
      customUnit === "months"
        ? "month"
        : customUnit === "years"
          ? "year"
          : "day";
    return customValue === 1
      ? `${customValue} ${unitSingular}`
      : `${customValue} ${unitSingular}s`;
  }

  // Priority 2: known presets
  const preset = PRESET_DURATION_LABELS[days];
  if (preset) return preset;

  // Priority 3: heuristic from days (matches backend convention: months=30, years=365)
  if (days >= 365 && days % 365 === 0) {
    const years = days / 365;
    return `${years} ${years === 1 ? "year" : "years"}`;
  }
  if (days >= 30 && days % 30 === 0) {
    const months = days / 30;
    return `${months} ${months === 1 ? "month" : "months"}`;
  }
  return `${days} ${days === 1 ? "day" : "days"}`;
}

// ============================================================================
// Usage Rights Badges
// ============================================================================

export function PerpetualUsageRightsBadge() {
  return (
    <div className="flex flex-col gap-1 w-fit">
      <div className="flex items-center gap-1 px-2 py-0.5 bg-success rounded-lg">
        <InfinityIcon className="w-3 h-3 text-success-foreground" />
        <span className="text-xs font-normal text-success-foreground whitespace-nowrap">
          Perpetual license
        </span>
      </div>
    </div>
  );
}

export function NoUsageRightsBadge() {
  return (
    <div className="flex flex-col gap-1 w-fit">
      <div className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded-lg">
        <Ban className="w-3 h-3 text-muted-foreground" />
        <span className="text-xs font-normal text-muted-foreground whitespace-nowrap">
          No usage rights
        </span>
      </div>
    </div>
  );
}

interface TimeBoundUsageRightsBadgeProps {
  /** Duration label e.g. "3 months", "1 year", "45 days" */
  label: string;
  /** Formatted expiration date e.g. "6/02/2026" */
  expirationDate?: string | null;
}

export function TimeBoundUsageRightsBadge({
  label,
  expirationDate,
}: TimeBoundUsageRightsBadgeProps) {
  return (
    <div className="flex flex-col gap-1 w-fit">
      <div className="flex items-center gap-1 px-2 py-0.5 bg-info rounded-lg">
        <Clock className="w-3 h-3 text-info-foreground" />
        <span className="text-xs font-normal text-info-foreground whitespace-nowrap">
          Usage Rights: {label}
        </span>
      </div>
      {expirationDate && (
        <div className="flex items-center gap-1 px-2">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs font-normal text-muted-foreground whitespace-nowrap">
            Valid through {expirationDate}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Usage Rights Badge Selector
// ============================================================================

interface UsageRightsBadgeSelectorProps {
  /** Usage rights duration in days (-2 = perpetual, -1 = none, null = not set) */
  usageRightsDuration: number | null | undefined;
  /** Date the content was approved (ISO string or timestamp) */
  dateApproved: string | number | null | undefined;
  /** Date the content was first uploaded/submitted (ISO string or timestamp) */
  dateFirstUpload?: string | number | null | undefined;
  /** Whether the deliverable requires content approval (GCR) */
  requiresApproval?: boolean;
  /** Custom duration value from agreement (e.g. 2 for "2 months") */
  customValue?: number | null;
  /** Custom duration unit from agreement ("days", "months", "years") */
  customUnit?: string | null;
}

/**
 * Renders the appropriate usage rights badge based on duration and dates.
 *
 * Start date logic (per ticket AC3):
 * - If content approval required → Start Date = Approval Date
 * - If content approval NOT required → Start Date = Submission Date
 */
export function UsageRightsBadgeSelector({
  usageRightsDuration,
  dateApproved,
  dateFirstUpload,
  requiresApproval = true,
  customValue,
  customUnit,
}: UsageRightsBadgeSelectorProps) {
  // Perpetual usage rights (-2)
  if (usageRightsDuration === -2) {
    return <PerpetualUsageRightsBadge />;
  }

  // No usage rights configured (-1, 0, null/undefined)
  if (
    usageRightsDuration === null ||
    usageRightsDuration === undefined ||
    usageRightsDuration <= 0
  ) {
    return <NoUsageRightsBadge />;
  }

  // Time-based usage rights (positive duration in days)
  const label = formatDurationLabel(
    usageRightsDuration,
    customValue,
    customUnit,
  );

  // Determine start date based on approval requirement
  // Non-GCR: only use submission date (never fall back to approval date)
  const rawStartDate = requiresApproval ? dateApproved : dateFirstUpload;

  // No start date available yet — show label without expiration
  if (!rawStartDate) {
    return <TimeBoundUsageRightsBadge label={label} />;
  }

  // Parse start date (UTC-safe to avoid timezone drift)
  const startDate =
    typeof rawStartDate === "number"
      ? parseISO(new Date(rawStartDate * 1000).toISOString())
      : parseISO(rawStartDate as string);

  // Calculate and format expiration date
  const expirationDate = addDays(startDate, usageRightsDuration);
  const formattedExpiration = format(expirationDate, "MMMM d, yyyy");

  return (
    <TimeBoundUsageRightsBadge
      label={label}
      expirationDate={formattedExpiration}
    />
  );
}
